import type { CrossUserPermissionSyncDto } from '@/libs/api-user-mgt/generated/model';
import type { UserPermissionsByDatabaseDto } from '@/libs/api-user-mgt/generated/model';

import type { PermissionFlagsWire } from '@/userManagement/domain/types/PermissionFlagsWire';

function databaseNameMatches(a: string, b: string): boolean {
  return a.trim().toLowerCase() === b.trim().toLowerCase();
}

function looksLikeUuid(value: string): boolean {
  const v = value.trim();
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(v);
}

/** True when the scope string is a databaseId (UUID) used with GET …/permissions items[]. */
export function isDatabaseIdScopeString(scope: string): boolean {
  return looksLikeUuid(scope);
}

/**
 * Picks permission flag rows from `UserPermissionsByDatabaseDto` for the requested database.
 * When `databaseName` is empty, merges rows across databases by `featureId` (last wins).
 */
export function pickPermissionFlagsFromUserPermissionsByDatabaseDto(
  dto: UserPermissionsByDatabaseDto | null | undefined,
  databaseName?: string
): PermissionFlagsWire[] {
  const items = dto?.items ?? [];
  const dbTrim = databaseName?.trim() ?? '';

  let rawRows: CrossUserPermissionSyncDto[] = [];
  if (dbTrim.length > 0) {
    let entry = items.find((x) => databaseNameMatches(x.tenantDatabaseName ?? '', dbTrim));
    if (!entry && looksLikeUuid(dbTrim)) {
      entry = items.find(
        (x) =>
          String(x.databaseId ?? '')
            .trim()
            .toLowerCase() === dbTrim.toLowerCase()
      );
    }
    rawRows = entry?.permissions ?? [];
  } else if (items.length === 1) {
    rawRows = items[0]?.permissions ?? [];
  } else {
    const byFeature = new Map<number, CrossUserPermissionSyncDto>();
    for (const it of items) {
      for (const p of it.permissions ?? []) {
        const fid = typeof p.featureId === 'number' ? p.featureId : parseInt(String(p.featureId ?? ''), 10);
        if (Number.isNaN(fid)) continue;
        byFeature.set(fid, { ...p, featureId: fid });
      }
    }
    rawRows = [...byFeature.values()];
  }

  const out: PermissionFlagsWire[] = [];
  for (const p of rawRows) {
    const featureId = typeof p.featureId === 'number' ? p.featureId : parseInt(String(p.featureId ?? ''), 10);
    if (Number.isNaN(featureId)) continue;
    out.push({
      featureId,
      allowRead: Boolean(p.allowRead),
      allowWrite: Boolean(p.allowWrite),
      allowDelete: Boolean(p.allowDelete),
      allowCentralAccess: Boolean(p.allowCentralAccess),
      allowExcelExport: Boolean(p.allowExcelExport)
    });
  }
  return out;
}
